import React from "react";
import ReactGA from "react-ga4";
import { useTranslation } from "react-i18next";

import { useMe } from "../../../../hooks/queries";
import { LocationReviewSummary } from "../../../../models/LocationReviewSummary";
import DataSvc from "../../../../services/dataSvc";
import { buildCsvFilename } from "../../../../utils/csvUtils";
import { TextFileDownload } from "../../../Common/TextFileDownload";

type IDownloadReviewsButtonsSectionProps = {
  locationTitle?: string;
  reviewSummary: LocationReviewSummary;
};

export const DownloadReviewsButtonsSection: React.FunctionComponent<
  IDownloadReviewsButtonsSectionProps
> = ({ locationTitle, reviewSummary }) => {
  const { t: _t } = useTranslation();
  const t = (key: string) =>
    _t(
      `facilityReviewSummariesPage.extendedReviewSummary.downloadReviewsButton.${key}`
    );
  const { data: me } = useMe();

  const { id: summaryId, location_id } = reviewSummary;

  const downloadAndProcessReviews = async () => {
    const reviews = await DataSvc.getLocationReviewSummaryReviews(
      Number(location_id),
      Number(summaryId)
    );

    const escapeCsvField = (value: unknown) =>
      `"${String(value ?? "").replace(/"/g, '""')}"`;

    const header = ["日付", "投稿者", "点数", "文章", "言語"]
      .map(escapeCsvField)
      .join(",");

    const processedReviews = (reviews as any[]).map(
      ({ review_date, rating, comment, language, reviewer_name }) => {
        return [
          review_date.split("T")[0],
          reviewer_name,
          rating,
          comment,
          language,
        ]
          .map(escapeCsvField)
          .join(",");
      }
    );

    const csvRows = [header, ...processedReviews];

    const startDateStr = `${reviewSummary.start_date}`.split("T")[0];
    const endDateStr = `${reviewSummary.end_date}`.split("T")[0];

    // Build standardized filename
    const filename = buildCsvFilename(
      {
        startDate: startDateStr,
        endDate: endDateStr,
        organizationName: me?.organization?.name,
        facilityName: locationTitle,
        facilityId: Number(location_id),
        contentDescription: "review_summary",
      },
      _t
    );

    ReactGA.event({
      category: "review-summaries",
      action: "download-reviews",
    });

    return { filename, content: csvRows.join("\n") };
  };

  return (
    <div className="download-reviews-button white-border-panel section">
      <h2 className="section-title">{t("download_relevant_reviews")}</h2>
      <TextFileDownload
        queryKey={["reviews-csv"]}
        queryFunction={downloadAndProcessReviews}
      />
    </div>
  );
};
